import { AlertTriangle } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import {
  ERROR_RECENT_WINDOW_DAYS,
  QUEUE_DEPTH_WARN_THRESHOLD,
} from '@/types/admin-queue-metrics.types';
import { useAdminQueueMetrics } from '../hooks/useAdminQueueMetrics';
import { formatAgeSec } from './formatAge';
import { KPICard } from './KPICard';

interface QueueHealthProps {
  password: string;
}

/**
 * Queue Health — pgmq depth, the retry/ERROR/HELD backlog, and pipeline
 * throughput (YAR-208).
 *
 * Renders its own loading and error states from its own hook. A failure here
 * must not blank the rest of the dashboard, which is exactly the situation
 * this section exists to report on.
 */
export function QueueHealth({ password }: QueueHealthProps) {
  const {
    status,
    data,
    error,
    lastFetchedAt,
    refetch,
    backpressureAlertActive,
  } = useAdminQueueMetrics(password);

  const body = () => {
    if (status === 'loading' && !data) {
      return (
        <div className="text-muted-foreground">Loading queue metrics…</div>
      );
    }
    if (status === 'error' && !data) {
      return (
        <Card className="p-4 border-danger">
          <div className="text-sm text-danger">
            Failed to load queue metrics: {error}
          </div>
          <Button
            variant="secondary"
            onClick={refetch}
            className="mt-3"
          >
            Retry
          </Button>
        </Card>
      );
    }
    if (!data) return null;

    const { queues, pipeline } = data;
    const processing = queues.email_processing;
    const retry = queues.email_retry;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Card 1 — email_processing */}
        <Card className="p-4 flex flex-col gap-3">
          <div className="text-sm font-medium">email_processing</div>
          <div className="grid grid-cols-2 gap-3">
            <KPICard
              label="Queue depth"
              value={processing.depth}
              tone={
                processing.depth > QUEUE_DEPTH_WARN_THRESHOLD
                  ? 'warning'
                  : 'default'
              }
              hint={`${processing.total_messages} total ever`}
            />
            <KPICard
              label="Oldest message"
              value={formatAgeSec(processing.oldest_msg_age_sec)}
              hint={`newest ${formatAgeSec(processing.newest_msg_age_sec)}`}
            />
          </div>
          {backpressureAlertActive ? (
            <div className="flex items-start gap-2 rounded-md border border-danger p-3 text-sm text-danger">
              <AlertTriangle
                className="h-4 w-4 mt-0.5 shrink-0"
                aria-hidden="true"
              />
              <span>
                Queue depth has exceeded {QUEUE_DEPTH_WARN_THRESHOLD} for more
                than 5 minutes — processing may be backed up.
              </span>
            </div>
          ) : null}
        </Card>

        {/* Card 2 — email_retry + the parked backlog */}
        <Card className="p-4 flex flex-col gap-3">
          <div className="text-sm font-medium">email_retry &amp; backlog</div>
          <div className="grid grid-cols-2 gap-3">
            <KPICard
              label="Retry queue depth"
              value={retry.depth}
              tone={retry.depth > 0 ? 'warning' : 'default'}
              hint={`oldest ${formatAgeSec(retry.oldest_msg_age_sec)}`}
            />
            <KPICard
              label={`ERROR emails (${ERROR_RECENT_WINDOW_DAYS}d)`}
              value={pipeline.recent_error_email_count}
              tone={
                pipeline.recent_error_email_count > 0 ? 'warning' : 'default'
              }
              hint={`${pipeline.error_email_count} all-time — nothing re-enqueues an ERROR email`}
            />
            <KPICard
              label="HELD emails"
              value={pipeline.held_email_count}
              hint="waiting out a transient LLM failure"
            />
            <KPICard
              label="HELD overdue"
              value={pipeline.held_overdue_count}
              tone={pipeline.held_overdue_count > 0 ? 'warning' : 'default'}
              hint="past held_until, or with no deadline at all"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            Retry queue messages are re-enqueued every 5 minutes by the cleanup
            cron.
          </div>
        </Card>

        {/* Card 3 — pipeline throughput */}
        <Card className="p-4 flex flex-col gap-3">
          <div className="text-sm font-medium">Pipeline throughput</div>
          <div className="grid grid-cols-2 gap-3">
            <KPICard
              label="Avg processing time"
              value={formatAgeSec(pipeline.avg_processing_sec)}
              hint="received → extraction succeeded"
            />
            <KPICard
              label="Processed (24h)"
              value={pipeline.completed_last_24h}
            />
          </div>
        </Card>
      </div>
    );
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Queue health</h2>
        <div className="text-xs text-muted-foreground">
          {lastFetchedAt ? lastFetchedAt.toLocaleTimeString() : '—'}
        </div>
      </div>
      {body()}
      <div className="text-xs text-muted-foreground">
        Auto-refreshing every 30s · Cron runs every 1 min (process) / 10 min
        (cleanup)
      </div>
    </section>
  );
}
